import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import { useToast } from '@/hooks/use-toast'
import { fetchExport } from '../api/form-io'
import { exportFilename, type ExportScope, type ExportSource } from '../api/form-io-core'

export interface ExportTarget {
  projectId: string
  scope: ExportScope
  groupId?: string
  formId?: string
  /** Human label for the dialog title, e.g. the group or form name. */
  label: string
  /** Used for the download filename. */
  projectTitle?: string
}

const SCOPE_LABEL: Record<ExportScope, string> = {
  project: 'کل پروژه',
  group: 'گروه فرم',
  form: 'فرم',
}

export function ExportDialog({
  target,
  open,
  onClose,
}: {
  target: ExportTarget | null
  open: boolean
  onClose: () => void
}) {
  const { toast } = useToast()
  const [source, setSource] = useState<ExportSource>('published')
  const [busy, setBusy] = useState(false)

  if (!target) return null

  const download = async () => {
    setBusy(true)
    try {
      const envelope = await fetchExport({
        projectId: target.projectId,
        scope: target.scope,
        source,
        groupId: target.groupId,
        formId: target.formId,
      })
      const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = exportFilename(target.projectTitle, target.scope)
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      const total = envelope.groups.reduce((n, g) => n + g.forms.length, 0)
      toast({ title: 'خروجی آماده شد', description: `${total} فرم در ${envelope.groups.length} گروه دانلود شد.` })
      onClose()
    } catch (e) {
      toast({ variant: 'destructive', title: 'خطا در خروجی', description: (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>خروجی گرفتن: {SCOPE_LABEL[target.scope]}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            «{target.label}» به‌صورت یک فایل JSON دانلود می‌شود (شامل گروه‌ها، فرم‌ها،
            فیلدها، شرط‌ها و اعتبارسنجی‌ها).
          </p>

          <div>
            <p className="text-sm font-medium mb-2">منبع تعریف فرم</p>
            <div className="grid grid-cols-2 gap-2">
              <SourceOption
                active={source === 'published'}
                onClick={() => setSource('published')}
                title="نسخهٔ منتشرشده"
                desc="تعریف پایدارِ در حال استفاده"
              />
              <SourceOption
                active={source === 'draft'}
                onClick={() => setSource('draft')}
                title="پیش‌نویس"
                desc="آخرین تغییرات منتشرنشده"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              اگر منبع انتخابی برای فرمی موجود نباشد، منبع دیگر استفاده می‌شود.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            انصراف
          </Button>
          <Button onClick={download} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            دانلود JSON
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SourceOption({
  active,
  onClick,
  title,
  desc,
}: {
  active: boolean
  onClick: () => void
  title: string
  desc: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-start rounded-lg border p-3 transition-colors',
        active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/30',
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </button>
  )
}
